import axios from 'axios';
import { AuthService } from './auth.service';

// Create axios instance
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: 60000, // เพิ่ม timeout
    maxContentLength: 10 * 1024 * 1024, // 10MB limit
    maxBodyLength: 10 * 1024 * 1024
});

// สร้างตัวแปรเก็บสถานะการ refresh token เพื่อป้องกัน multiple refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = AuthService.getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // เพิ่ม timestamp เพื่อป้องกัน cache
        config.params = {
            ...config.params,
            _t: Date.now()
        };
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // ถ้าไม่มี response หรือไม่มี status ให้ reject เลย
        if (!error.response || !error.response.status) {
            return Promise.reject(error);
        }

        if (error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // ถ้ากำลัง refresh อยู่ ให้เข้าคิวรอ
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = AuthService.getRefreshToken();
                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/auth/refresh-token`,
                    { refreshToken },
                    { timeout: 10000 } // เพิ่ม timeout สำหรับ refresh token request
                );

                const { accessToken, refreshToken: newRefreshToken } = response.data;
                AuthService.setTokens({ accessToken, refreshToken: newRefreshToken });

                // Process queued requests
                processQueue(null, accessToken);
                
                // Update authorization header
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                AuthService.clearAuth();
                
                // เพิ่มการเช็คว่าอยู่ที่หน้า login หรือไม่
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Handle other error statuses
        if (error.response.status === 403) {
            // Handle forbidden error
            console.error('Access forbidden:', error);
        }

        return Promise.reject(error);
    }
);

// Add response timeout handler
api.defaults.timeoutErrorMessage = 'Request timeout';

export default api;